/**
 * Preview of the station code and label the server will assign.
 *
 * This mirrors new-intake-server/utils/stationName.js so the create and edit
 * forms can show what a device will be called before it is saved. The server
 * is authoritative — it also allocates the unit number when one is left blank
 * and enforces uniqueness — so treat anything here as a preview only.
 */

const AREA_WORD_CODES = {
  room: 'RM',
  suite: 'ST',
  office: 'OF',
};

const titleCase = (str) =>
  str.replace(/\S+/g, (word) =>
    /^\d+$/.test(word) ? word : word[0].toUpperCase() + word.slice(1).toLowerCase()
  );

export function parseAreaInput(input) {
  const raw = String(input || '').trim().replace(/\s+/g, ' ');
  if (!raw) return { area: '', unitNo: null };

  const match = raw.match(/^(.*?)[\s#-]+(\d{1,2})$/);
  if (match && match[1].trim()) {
    return { area: titleCase(match[1].trim()), unitNo: parseInt(match[2], 10) };
  }
  return { area: titleCase(raw), unitNo: null };
}

export function deriveAreaCode(area) {
  const words = String(area || '').trim().split(/[\s_/-]+/).filter(Boolean);
  if (words.length === 0) return '';

  let code;
  if (words.length === 1) {
    const word = words[0];
    code = /^\d+$/.test(word) ? word : (AREA_WORD_CODES[word.toLowerCase()] || word.slice(0, 3));
  } else {
    code = words
      .map((word, i) => {
        if (/^\d+$/.test(word)) return word;
        if (i === 0 && AREA_WORD_CODES[word.toLowerCase()]) return AREA_WORD_CODES[word.toLowerCase()];
        return word[0];
      })
      .join('');
  }

  return code.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
}

export const buildStationCode = (areaCode, unitNo) =>
  `${areaCode}-${String(unitNo).padStart(2, '0')}`;

export const buildLabel = (area, unitNo) =>
  (unitNo === 1 && /\d$/.test(area)) ? area : `${area} ${unitNo}`;

/**
 * What the station will be called. Returns null when there is not enough typed
 * yet to say, so the form can simply hide the preview.
 */
export function previewStation(areaInput, unitInput) {
  const parsed = parseAreaInput(areaInput);
  if (!parsed.area) return null;

  const areaCode = deriveAreaCode(parsed.area);
  if (!areaCode) return null;

  const typedUnit = unitInput === '' || unitInput === null || unitInput === undefined
    ? null
    : parseInt(unitInput, 10);

  if (typedUnit !== null && (isNaN(typedUnit) || typedUnit < 1 || typedUnit > 99)) return null;

  const unit = typedUnit !== null ? typedUnit : parsed.unitNo;

  // With no number anywhere the server picks the next free one in this area,
  // so the preview shows the shape rather than guessing the digits.
  if (unit === null) {
    return {
      area: parsed.area,
      areaCode,
      code: `${areaCode}-##`,
      label: `${parsed.area} …`,
      autoNumbered: true,
    };
  }

  return {
    area: parsed.area,
    areaCode,
    code: buildStationCode(areaCode, unit),
    label: buildLabel(parsed.area, unit),
    autoNumbered: false,
  };
}
